import { gql } from "@apollo/client";
import {
  DataConnectionId,
  DataSourceSchemaId,
  DataSourceTableId,
  SemanticViewId,
  assertNever,
  getDateTimeString,
} from "@hex/common";
import { useCallback } from "react";

import { useDispatch } from "../../../redux/hooks.js";
import { setNodePinned } from "../../../redux/slices/schemaTreeBrowserSlice.js";
import { useProjectContext } from "../../../util/projectContext.js";
import { useToaster } from "../../common/Toasts.js";
import {
  PinnedSchemasDocument,
  PinnedSchemasQuery,
  PinnedSchemasQueryVariables,
  PinnedSemanticDatasetFragment,
  PinnedSemanticDatasetsDocument,
  PinnedSemanticDatasetsQuery,
  PinnedSemanticDatasetsQueryVariables,
  PinnedTablesDocument,
  PinnedTablesQuery,
  PinnedTablesQueryVariables,
} from "../../sidebar/data-browser/FavoritesView.generated.js";

import {
  usePinSchemaMutation,
  usePinSemanticDatasetMutation,
  usePinTableMutation,
} from "./queries.generated.js";
import {
  PinnedSemanticViewsCacheUpdateDocument,
  PinnedSemanticViewsCacheUpdateQuery,
  PinnedSemanticViewsCacheUpdateQueryVariables,
  usePinSemanticViewMutation,
} from "./usePinData.generated.js";

gql`
  mutation pinSemanticView(
    $semanticViewId: SemanticViewId!
    $pinned: Boolean!
  ) {
    pinSemanticView(semanticViewId: $semanticViewId, pinned: $pinned) {
      id
      pinned
      pinnedAt
    }
  }

  query PinnedSemanticViewsCacheUpdate {
    me {
      id
      pinnedSemanticViews {
        id
        pinned
        pinnedAt
      }
    }
  }
`;

interface BaseStarArgs {
  newPinnedState: boolean;
  kind: "table" | "schema" | "semanticDataset" | "semanticView";
}

type PinTableArgs = BaseStarArgs & {
  kind: "table";
  id: DataSourceTableId;
  idPath: (string | number)[];
  multiDatabase: boolean;
  dataConnectionId: DataConnectionId;
};

type PinSchemaArgs = BaseStarArgs & {
  kind: "schema";
  id: DataSourceSchemaId;
  idPath: (string | number)[];
  multiDatabase: boolean;
  dataConnectionId: DataConnectionId;
};

export type PinSemanticDatasetArgs = BaseStarArgs & {
  kind: "semanticDataset";

  // we overwrite `pinned` and `pinnedAt` when we use `originalDataset`, so we don't need them
  originalDataset: Omit<PinnedSemanticDatasetFragment, "pinned" | "pinnedAt">;
};

export type PinSemanticViewArgs = BaseStarArgs & {
  kind: "semanticView";
  semanticViewId: SemanticViewId;
};

type PinDataArgs =
  | PinSchemaArgs
  | PinTableArgs
  | PinSemanticDatasetArgs
  | PinSemanticViewArgs;

export function usePinData(): (args: PinDataArgs) => Promise<void> {
  const dispatch = useDispatch();
  const [pinSchema] = usePinSchemaMutation();
  const [pinTable] = usePinTableMutation();
  const [pinSemanticDataset] = usePinSemanticDatasetMutation();
  const [pinSemanticView] = usePinSemanticViewMutation();
  const toaster = useToaster();
  const projectContext = useProjectContext({ safe: true });

  return useCallback(
    async (args: PinDataArgs) => {
      const newPinnedState = args.newPinnedState;

      if (args.kind === "table") {
        const { dataConnectionId, id, idPath, multiDatabase } = args;
        try {
          dispatch(
            setNodePinned({
              dataConnectionId,
              pinned: newPinnedState,
              idPath,
              multiDatabase,
              kind: "table",
            }),
          );
          await pinTable({
            variables: {
              dataConnectionId,
              dataSourceTableId: id,
              pinned: newPinnedState,
            },
            update: async (cache, { data }) => {
              // update pinned query
              const pinnedQueryResult = cache.readQuery<
                PinnedTablesQuery,
                PinnedTablesQueryVariables
              >({
                query: PinnedTablesDocument,
                variables: {
                  hexId: projectContext?.hexId ?? null,
                },
              });

              const me = pinnedQueryResult?.me;
              if (me != null && data != null) {
                const newPinnedTables = newPinnedState
                  ? [...me.pinnedTables, data.pinTable]
                  : me.pinnedTables.filter((t) => t.id !== id);

                cache.writeQuery<PinnedTablesQuery, PinnedTablesQueryVariables>(
                  {
                    query: PinnedTablesDocument,
                    variables: {
                      hexId: projectContext?.hexId ?? null,
                    },
                    data: {
                      __typename: "Query",
                      me: {
                        ...me,
                        pinnedTables: newPinnedTables,
                      },
                    },
                  },
                );
              }
            },
          });
        } catch (e) {
          console.error(e);
          toaster.show({
            message: "Error favoriting table",
            intent: "danger",
          });
          // revert redux state in case of error
          dispatch(
            setNodePinned({
              dataConnectionId,
              multiDatabase,
              pinned: !newPinnedState,
              idPath,
              kind: "table",
            }),
          );
        }
      } else if (args.kind === "schema") {
        const { dataConnectionId, id, idPath, multiDatabase } = args;
        try {
          dispatch(
            setNodePinned({
              dataConnectionId,
              pinned: newPinnedState,
              idPath,
              multiDatabase,
              kind: "schema",
            }),
          );
          await pinSchema({
            variables: {
              dataConnectionId,
              dataSourceSchemaId: id,
              pinned: newPinnedState,
            },
            update: (cache, { data }) => {
              // update pinned query
              const pinnedQueryResult = cache.readQuery<
                PinnedSchemasQuery,
                PinnedSchemasQueryVariables
              >({
                query: PinnedSchemasDocument,
                variables: {
                  hexId: projectContext?.hexId ?? null,
                },
              });

              const me = pinnedQueryResult?.me;
              if (me != null && data != null) {
                const newPinnedSchemas = newPinnedState
                  ? [...me.pinnedSchemas, data.pinSchema]
                  : me.pinnedSchemas.filter((s) => s.id !== id);

                cache.writeQuery<
                  PinnedSchemasQuery,
                  PinnedSchemasQueryVariables
                >({
                  query: PinnedSchemasDocument,
                  data: {
                    __typename: "Query",
                    me: {
                      ...me,
                      pinnedSchemas: newPinnedSchemas,
                    },
                  },
                  variables: {
                    hexId: projectContext?.hexId ?? null,
                  },
                });
              }
            },
          });
        } catch (e) {
          console.error(e);
          toaster.show({
            message: "Error pinning schema",
            intent: "danger",
          });
          // revert redux state in case of error
          dispatch(
            setNodePinned({
              dataConnectionId,
              multiDatabase,
              pinned: !newPinnedState,
              idPath,
              kind: "schema",
            }),
          );
        }
      } else if (args.kind === "semanticDataset") {
        const { originalDataset } = args;
        try {
          await pinSemanticDataset({
            variables: {
              semanticDatasetId: originalDataset.id,
              pinned: newPinnedState,
            },
            optimisticResponse: () => {
              return {
                __typename: "Mutation",
                pinSemanticDataset: {
                  ...args.originalDataset,
                  __typename: "SemanticDataset",
                  pinned: newPinnedState,
                  pinnedAt: newPinnedState
                    ? getDateTimeString(new Date())
                    : null,
                },
              };
            },
            update: async (cache, { data }) => {
              const pinnedDatasetsQueryResult = cache.readQuery<
                PinnedSemanticDatasetsQuery,
                PinnedSemanticDatasetsQueryVariables
              >({
                query: PinnedSemanticDatasetsDocument,
              });

              const me = pinnedDatasetsQueryResult?.me;
              if (me != null && data != null) {
                const newPinnedDatasets = newPinnedState
                  ? [...me.pinnedSemanticDatasets, data.pinSemanticDataset]
                  : me.pinnedSemanticDatasets.filter(
                      (s) => s.id !== originalDataset.id,
                    );
                cache.writeQuery<
                  PinnedSemanticDatasetsQuery,
                  PinnedSemanticDatasetsQueryVariables
                >({
                  query: PinnedSemanticDatasetsDocument,
                  data: {
                    __typename: "Query",
                    me: {
                      ...me,
                      pinnedSemanticDatasets: newPinnedDatasets,
                    },
                  },
                });
              }
            },
          });
        } catch (e) {
          console.error(e);
          toaster.show({
            message: "Error favoriting model",
            intent: "danger",
          });
        }
      } else if (args.kind === "semanticView") {
        const { semanticViewId } = args;
        try {
          await pinSemanticView({
            variables: { semanticViewId, pinned: newPinnedState },
            optimisticResponse: () => ({
              __typename: "Mutation",
              pinSemanticView: {
                __typename: "SemanticView",
                id: semanticViewId,
                pinned: newPinnedState,
                pinnedAt: newPinnedState ? getDateTimeString(new Date()) : null,
              },
            }),
            update: (cache, { data }) => {
              const pinnedViewsQueryResult = cache.readQuery<
                PinnedSemanticViewsCacheUpdateQuery,
                PinnedSemanticViewsCacheUpdateQueryVariables
              >({
                query: PinnedSemanticViewsCacheUpdateDocument,
              });

              const me = pinnedViewsQueryResult?.me;
              if (me != null && data != null) {
                const newPinnedViews = newPinnedState
                  ? [...me.pinnedSemanticViews, data.pinSemanticView]
                  : me.pinnedSemanticViews.filter(
                      (v) => v.id !== semanticViewId,
                    );

                cache.writeQuery({
                  query: PinnedSemanticViewsCacheUpdateDocument,
                  data: {
                    __typename: "Query",
                    me: { ...me, pinnedSemanticViews: newPinnedViews },
                  },
                });
              }
            },
          });
        } catch (e) {
          console.error(e);
          toaster.show({
            message: "Error favoriting view",
            intent: "danger",
          });
        }
      } else {
        assertNever(args, args);
      }
    },
    [
      dispatch,
      pinSchema,
      pinSemanticDataset,
      pinSemanticView,
      pinTable,
      projectContext?.hexId,
      toaster,
    ],
  );
}
